/**
 * Paleta de los biomas. Todo procedural, sin un solo asset descargado: cero
 * problemas de copyright y cero bytes de imagenes en el bundle.
 *
 * El viaje es ciudad -> estratosfera -> espacio, y el color cuenta esa historia
 * sin que haya que explicarla. Cuando el jugador nota que el color cambio, ya
 * lleva 1.500 metros.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ESTA PALETA ES ASI (1-oct-2026)
 * ---------------------------------------------------------------------------
 * La primera version era gris azulada en los tres biomas, con todo pintado con
 * la misma luz plana. El dueno lo miro como jugador y dijo lo que habia que
 * decir: **los objetos no se definen**. Tenia razon, y el problema no era que
 * faltara detalle: era que no habia CONTRASTE. Un obstaculo mortal y un
 * edificio del fondo tenian el mismo peso visual, asi que el ojo no sabia donde
 * mirar.
 *
 * Lo que arregla eso, y lo que ordena este fichero:
 *
 *   1. **El suelo es casi negro.** Todo lo que importa va encima, brillando.
 *      Si el suelo fuera gris medio, nada podria destacar sobre el.
 *   2. **Lo que brilla no se ilumina: se pinta ya brillante.** Los carriles,
 *      las monedas y los filos de los obstaculos usan materiales sin luz
 *      (`MeshBasicMaterial`), que salen a todo color pase lo que pase con las
 *      luces. Ademas de verse mejor, es mas barato que iluminarlos.
 *   3. **Luz de contorno (`rim`).** Una segunda luz de color, desde detras y
 *      arriba, que dibuja un filo en el borde de cada objeto. Es el truco mas
 *      barato que existe para separar una figura del fondo, y es la mitad de
 *      por que un juego parece caro.
 *   4. **El cielo es un degradado, no un color.** Un color plano de fondo se
 *      lee como "no han hecho el cielo". Un degradado da profundidad gratis.
 *
 * Los tres biomas usan el mismo esquema con distinta historia de color:
 * ciudad de noche, amanecer sobre las nubes, y espacio profundo.
 */

import type { Bioma } from './nivel';

export interface PaletaBioma {
  /** Parte alta del cielo. El degradado va de aqui al horizonte. */
  cenit: number;
  /** Parte baja del cielo, donde nace la pista. Es el color que da el ambiente. */
  horizonte: number;
  /**
   * Cuanto sube el color del horizonte por el cielo.
   *
   * Por encima de 1 sube mucho (bueno en la ciudad: el resplandor tiene que
   * llegar lo bastante alto para que los edificios se recorten contra el). Por
   * debajo de 1 se queda pegado al suelo, que es lo que quiere un amanecer: una
   * franja calida abajo y cielo profundo arriba.
   *
   * Es por bioma porque un solo numero no vale para los tres: con 1,6 la
   * estratosfera se convertia en una PARED ROJA de arriba abajo.
   */
  curvaCielo: number;
  /** Niebla: oculta el borde donde nacen los obstaculos, en vez de disimularlo. */
  niebla: number;
  nieblaCerca: number;
  nieblaLejos: number;
  /** Color del suelo / carretera. Casi negro a proposito: ver nota de arriba. */
  suelo: number;
  /** Lineas de los carriles. Van en material sin luz: son neon. */
  lineas: number;
  /** Los raíles de neon de los lados de la pista. */
  railes: number;
  /** Decorados de los lados (edificios, nubes, asteroides). */
  decorado: number[];
  /** Las luces de las ventanas / el brillo de los decorados. Sin luz, neon. */
  decoradoBrillo: number;
  /** Luz ambiental y direccional. */
  luzAmbiente: number;
  luzDireccional: number;
  intensidadAmbiente: number;
  intensidadDireccional: number;
  /** Luz de contorno: la que separa las figuras del fondo. */
  luzContorno: number;
  intensidadContorno: number;
  /** Si el bioma pinta estrellas. */
  estrellas: boolean;
}

export const PALETAS: Record<Bioma, PaletaBioma> = {
  // Ciudad de noche. Morado de contaminacion luminica en el horizonte y cian
  // en la pista: el contraste frio-calido es lo que hace que se vea electrico.
  ciudad: {
    // ---------------------------------------------------------------------
    // CIUDAD DE DIA (22-oct-2026)
    // ---------------------------------------------------------------------
    // Era una ciudad de noche en neon. Se veia elegante y estaba equivocada:
    // el publico de este juego son ninos y adolescentes, y el neon oscuro es
    // el lenguaje visual de un juego de adultos. Un crio asocia oscuro con
    // aburrido. Los runners que funcionan con ese publico —Subway Surfers el
    // primero— son de DIA, con colores saturados y todo luminoso.
    //
    // La regla que ordena esta paleta: **nada se lee por brillar en la
    // oscuridad, todo se lee por su color**. Al reves que antes.
    cenit: 0x2196f3,
    horizonte: 0xb3e5fc,
    // Baja: el azul fuerte se queda arriba y el horizonte queda claro, que es
    // como se ve un cielo de dia de verdad.
    curvaCielo: 0.7,
    niebla: 0xcfeaf7,
    // La niebla empieza LEJOS a proposito. Con 45 se comia la media distancia y
    // los obstaculos llegaban como puntos de confeti: el jugador los veia pero
    // no sabia cual era cual hasta que los tenia encima, que es tarde. La
    // niebla esta para ocultar el borde donde nacen las cosas, no para tapar lo
    // que hay que esquivar.
    nieblaCerca: 110,
    nieblaLejos: 240,
    // Asfalto gris, no negro. El suelo ocupa media pantalla: si es negro, media
    // pantalla esta apagada.
    suelo: 0x6b7280,
    lineas: 0xffffff,
    railes: 0xffc107,
    // Edificios de COLORES. Es lo que mas cambia la sensacion: una ciudad gris
    // es un sitio al que vas a trabajar; una de colores es un sitio de juego.
    decorado: [0xef5350, 0x42a5f5, 0xffca28, 0x66bb6a],
    decoradoBrillo: 0xffffff,
    luzAmbiente: 0xffffff,
    luzDireccional: 0xfff8e1,
    intensidadAmbiente: 0.95,
    intensidadDireccional: 1.15,
    // De dia la luz de contorno casi no hace falta: la silueta la da el color.
    luzContorno: 0x90caf9,
    intensidadContorno: 0.45,
    estrellas: false,
  },

  // Mediodia POR ENCIMA de las nubes.
  //
  // Era un amanecer: azul profundo arriba y una franja naranja abajo. Bonito
  // y, otra vez, de adulto. El dueno lo vio en pantalla el 22-oct-2026 y pidio
  // que los tres biomas fueran luminosos, «que el publico son ninos», asi que
  // esto paso de las seis de la manana a las doce del mediodia (D-019).
  //
  // Lo que da la sensacion de ALTURA ya no es la oscuridad del cielo: es que
  // las nubes estan DEBAJO. Un azul mas profundo arriba que en la ciudad, y
  // blanco deslumbrante en el horizonte, que es como se ve el cielo desde un
  // avion.
  estratosfera: {
    cenit: 0x1766d8,
    horizonte: 0xd8f0ff,
    // Baja, como la ciudad: el azul fuerte se queda arriba y el horizonte
    // claro. Con la curva alta esto era una pared de color de arriba abajo.
    curvaCielo: 0.7,
    niebla: 0xd8f0ff,
    nieblaCerca: 115,
    nieblaLejos: 255,
    // La pista, gris azulado claro. Era 0x16255c, un azul casi negro: media
    // pantalla apagada (la misma regla que oblido la ciudad de noche).
    suelo: 0x7b90ad,
    lineas: 0xffffff,
    railes: 0xffc107,
    // Nubes BLANCAS de verdad. Tienen que ser lo mas claro de la escena: es lo
    // que hace que se lean como nubes y no como rocas flotando.
    decorado: [0xffffff, 0xf2f9ff, 0xffffff, 0xe8f3ff],
    decoradoBrillo: 0xffffff,
    luzAmbiente: 0xffffff,
    luzDireccional: 0xfffdf2,
    intensidadAmbiente: 0.95,
    intensidadDireccional: 1.2,
    // De dia la luz de contorno casi no hace falta: la silueta la da el color.
    luzContorno: 0x9bd4ff,
    intensidadContorno: 0.45,
    // De dia no se ven estrellas. Dejarlas encendidas era lo que delataba que
    // este cielo no acababa de decidirse.
    estrellas: false,
  },

  // Espacio, pero el de un dibujo animado, no el de un documental.
  //
  // ---------------------------------------------------------------------
  // POR QUE UN ESPACIO LUMINOSO (22-oct-2026)
  // ---------------------------------------------------------------------
  // Este es el bioma al que mas le costo la regla del dueno, porque el
  // espacio de verdad ES negro. Pero el espacio de verdad tampoco tiene
  // carreteras, y aqui hay una.
  //
  // Era casi negro con una nebulosa violeta, y dos sesiones seguidas se fue
  // aclarando a parches porque «no se veia nada». Eso ya decia lo que
  // pasaba: el problema no era el tono, era la DECISION. Un crio que llega a
  // los 3.000 metros —lo mas lejos que ha llegado nunca— no se merece que el
  // premio sea una pantalla apagada (D-019).
  //
  // Lo que hace que siga siendo el espacio y no otro cielo: las estrellas,
  // la luna grande, los asteroides flotando y una nebulosa de color que no
  // existe en la Tierra. Ninguna de esas cosas necesita oscuridad.
  espacio: {
    cenit: 0x4a3bd0,
    horizonte: 0xc86bff,
    curvaCielo: 1.1,
    niebla: 0xbfa0f5,
    nieblaCerca: 110,
    nieblaLejos: 255,
    // La pista, violeta grisaceo y del mismo claro que el asfalto de la
    // ciudad. Era 0x121038: media pantalla negra.
    suelo: 0x6f68a0,
    lineas: 0xffffff,
    railes: 0x4ef0ff,
    // Asteroides en tonos claros y FRIOS. Claros porque son lo unico solido
    // del bioma y tienen que leerse; frios porque el naranja y el rojo estan
    // reservados a lo que mata (ver la nota de COLORES mas abajo), y un
    // decorado del color de un obstaculo hace dudar al jugador.
    decorado: [0xcdbdf5, 0xa8e8ff, 0xf3c9ff, 0xe8e0ff],
    decoradoBrillo: 0xffffff,
    luzAmbiente: 0xffffff,
    luzDireccional: 0xfff4ff,
    intensidadAmbiente: 0.95,
    intensidadDireccional: 1.15,
    luzContorno: 0x7be0ff,
    intensidadContorno: 0.5,
    // Las estrellas se quedan: son blancas y el cielo, aun siendo luminoso,
    // es de color. Sobre violeta se siguen leyendo, y son medio motivo por el
    // que esto parece el espacio.
    estrellas: true,
  },
};

/**
 * Colores de las piezas que no cambian con el bioma.
 *
 * El color dice que hacer antes de que se lea la forma:
 *
 *   rojo/naranja = te mata        azul/verde = te ayuda        oro = monedas
 *
 * ---------------------------------------------------------------------------
 * EL CRITERIO CAMBIO EL 22-OCT-2026, CON LA CIUDAD DE DIA
 * ---------------------------------------------------------------------------
 * **Antes: cuerpo oscuro, filo de neon.** Tenia sentido en una ciudad de
 * noche, donde lo oscuro se recorta contra el cielo y el neon es lo unico que
 * brilla.
 *
 * La ciudad paso a ser de dia (D-014) y eso dejo de valer. Sobre el asfalto
 * claro, un cuerpo casi negro no se lee como un objeto: se lee como una
 * SOMBRA o como un agujero. La vela roja —el obstaculo que hay que pasar por
 * debajo— era literalmente una caja negra con un borde rosa. Se vio jugando.
 *
 * **Ahora: el cuerpo lleva el color, y el filo lo remata.** Un rojo saturado
 * se lee como rojo de dia y de noche; un casi-negro solo se lee de noche. El
 * filo claro sigue estando, pero para marcar el borde —por donde se pasa—, no
 * para que el objeto exista.
 *
 * Vale igual para los tres biomas: un color saturado no necesita oscuridad
 * detras, y en la estratosfera y el espacio sigue recortando contra el fondo.
 */
export const COLORES = {
  // El personaje. Traje oscuro para que el casco y el motor sean lo que se ve;
  // naranja solo en los filos, que es el color de la marca.
  jugador: 0x1c2140,
  jugadorFilo: 0xff8a3d,
  jugadorCasco: 0x0e1430,
  jugadorVisor: 0x4de0ff,
  jugadorMotor: 0x4ec3ff,

  // Obstaculos: el CUERPO lleva el color de peligro, el filo lo remata.
  //
  // Los tres se quedan en la familia caliente —ambar y rojo— porque esa es la
  // regla de arriba y es mas importante que distinguirlos entre si: lo que
  // tiene que entenderse en una decima de segundo es «esto me mata», y lo que
  // hay que hacer con cada uno ya lo dice su sitio (la barrera esta en el
  // suelo y se salta, la vela flota a la altura del pecho y se pasa por
  // debajo, el tiburon ocupa el carril entero y hay que cambiarse).
  //
  // Se probo con el tiburon en AZUL para que se distinguiera mejor, y era un
  // error: en este juego el azul es el escudo, o sea «cogeme». Un obstaculo
  // del color de un premio es peor que un obstaculo parecido a otro.
  barrera: 0xffa41b,
  barreraFilo: 0xfff3d6,
  barreraDetalle: 0xd92d20,
  vela: 0xe8243c,
  velaFilo: 0xffd0d8,
  velaMecha: 0xff6b7d,
  tiburon: 0xb3202a,
  tiburonFilo: 0xffd166,
  // El vientre casi blanco es lo que le da forma de bicho: sin el, un cono
  // rojo entero es una mancha y no se sabe ni hacia donde mira.
  tiburonVientre: 0xffe3e0,
  tiburonDiente: 0xffffff,
  tiburonOjo: 0x16243a,

  // Recolectables: todos brillan, todos en colores que NO son los del peligro.
  moneda: 0xffd24a,
  monedaBorde: 0xff9d00,
  iman: 0xff4d6d,
  imanPunta: 0xffffff,
  x2: 0x7bdc5a,
  escudo: 0x4ec3ff,
  cohete: 0xff6b35,
  coheteLlama: 0xffd166,
  ballena: 0x4fa8d8,
  ballenaVientre: 0xd8eef8,
} as const;

/**
 * Mezcla entre dos biomas. Se usa en el cambio de bioma: en vez de un corte
 * seco, el color viaja durante unos metros. Un corte seco se lee como un fallo
 * grafico; una transicion se lee como que estas subiendo.
 */
export function mezclarPaletas(
  a: PaletaBioma,
  b: PaletaBioma,
  t: number
): PaletaBioma {
  const k = Math.max(0, Math.min(1, t));
  const num = (n1: number, n2: number): number => n1 + (n2 - n1) * k;
  const col = (c1: number, c2: number): number => mezclarColor(c1, c2, k);

  return {
    cenit: col(a.cenit, b.cenit),
    curvaCielo: num(a.curvaCielo, b.curvaCielo),
    horizonte: col(a.horizonte, b.horizonte),
    niebla: col(a.niebla, b.niebla),
    nieblaCerca: num(a.nieblaCerca, b.nieblaCerca),
    nieblaLejos: num(a.nieblaLejos, b.nieblaLejos),
    suelo: col(a.suelo, b.suelo),
    lineas: col(a.lineas, b.lineas),
    railes: col(a.railes, b.railes),
    decorado: a.decorado.map((c, i) =>
      col(c, b.decorado[i % b.decorado.length] as number)
    ),
    decoradoBrillo: col(a.decoradoBrillo, b.decoradoBrillo),
    luzAmbiente: col(a.luzAmbiente, b.luzAmbiente),
    luzDireccional: col(a.luzDireccional, b.luzDireccional),
    intensidadAmbiente: num(a.intensidadAmbiente, b.intensidadAmbiente),
    intensidadDireccional: num(a.intensidadDireccional, b.intensidadDireccional),
    luzContorno: col(a.luzContorno, b.luzContorno),
    intensidadContorno: num(a.intensidadContorno, b.intensidadContorno),
    // Las estrellas aparecen en cuanto el bioma de destino las tenga.
    estrellas: a.estrellas || b.estrellas,
  };
}

/** Mezcla dos colores hexadecimales, canal a canal. */
export function mezclarColor(c1: number, c2: number, k: number): number {
  const r1 = (c1 >> 16) & 0xff;
  const g1 = (c1 >> 8) & 0xff;
  const b1 = c1 & 0xff;
  const r2 = (c2 >> 16) & 0xff;
  const g2 = (c2 >> 8) & 0xff;
  const b2 = c2 & 0xff;
  const r = Math.round(r1 + (r2 - r1) * k);
  const g = Math.round(g1 + (g2 - g1) * k);
  const b = Math.round(b1 + (b2 - b1) * k);
  return (r << 16) | (g << 8) | b;
}
